import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";

const PublicAdminAuthContext = createContext(null);

export function PublicAdminAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("ame_padmin_token"));
  const [admin, setAdmin] = useState(() => {
    try {
      const cached = localStorage.getItem("ame_padmin_user");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(() => !localStorage.getItem("ame_padmin_token"));

  const fetchAdmin = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get("/public-admin/auth/me", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmin(res.data);
      localStorage.setItem("ame_padmin_user", JSON.stringify(res.data));
    } catch (err) {
      // ONLY logout if the backend explicitly rejects the token with 401 Unauthorized
      if (err?.response?.status === 401) {
        localStorage.removeItem("ame_padmin_token");
        localStorage.removeItem("ame_padmin_user");
        setToken(null);
        setAdmin(null);
      }
      // On network errors or timeouts, retain the cached admin session!
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchAdmin();
    else setLoading(false);
  }, [token, fetchAdmin]);

  const login = async (email, password) => {
    const res = await api.post("/public-admin/auth/login", { email, password });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem("ame_padmin_token", newToken);
    localStorage.setItem("ame_padmin_user", JSON.stringify(userData));
    setToken(newToken);
    setAdmin(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("ame_padmin_token");
    localStorage.removeItem("ame_padmin_user");
    setToken(null);
    setAdmin(null);
  };

  // Helper to get auth header
  const authHeader = () => ({ headers: { Authorization: `Bearer ${token}` } });

  return (
    <PublicAdminAuthContext.Provider value={{ admin, token, login, logout, loading, authHeader }}>
      {children}
    </PublicAdminAuthContext.Provider>
  );
}

export const usePublicAdmin = () => useContext(PublicAdminAuthContext);
